import { useCallback, useState } from 'react'

import { useLightMode } from '@/hooks/useLightMode'
import { useToasts } from '@/hooks/useToasts'
import * as Sentry from '@sentry/nextjs'
import { useTranslation } from 'react-i18next'

import { useCreateProductMessageMutation } from '@/graphql/generated/hooks'
import { CreateProductMessageInput } from '@/graphql/generated/schemas'

/**
 * Return type for useCreateProductMessage hook
 */
export interface UseCreateProductMessageReturnIProps {
  createProductMessage: (
    input: CreateProductMessageInput
  ) => Promise<{ success: boolean; error?: unknown }>
  isCreating: boolean
}

interface UseCreateProductMessageOptionsIProps {
  /**
   * Render toasts with the dark-package Alert. The caller (the rebrand
   * AnnouncementBannerSection) opts in; the legacy banner leaves it off so it
   * keeps the legacy toast component, since this hook is shared by both.
   */
  isRebrand?: boolean
}

/**
 * Hook that handles creation of product messages with proper error handling.
 */
export const useCreateProductMessage = ({
  isRebrand = false,
}: UseCreateProductMessageOptionsIProps = {}): UseCreateProductMessageReturnIProps => {
  const { t } = useTranslation()
  const [createMutation] = useCreateProductMessageMutation()
  const colorScheme = useLightMode()
  const { showSuccess, showError } = useToasts({ colorScheme, isRebrand })
  const [isCreating, setIsCreating] = useState(false)

  const createProductMessage = useCallback(
    async (
      input: CreateProductMessageInput
    ): Promise<{ success: boolean; error?: unknown }> => {
      setIsCreating(true)

      try {
        const result = await createMutation({
          variables: { input },
          refetchQueries: ['ProductMessages'],
          awaitRefetchQueries: true,
        })

        if (!result.data?.createProductMessage?.productMessage) {
          throw new Error('No data returned from mutation')
        }

        showSuccess(t('settings:rebrand_general_announcement_create_success'))

        return { success: true }
      } catch (error) {
        Sentry.captureException(error)
        showError(t('settings:rebrand_general_announcement_create_error'))

        return { success: false, error }
      } finally {
        setIsCreating(false)
      }
    },
    [createMutation, showSuccess, showError, t]
  )

  return {
    createProductMessage,
    isCreating,
  }
}
